// LusideaToolsShowcase.js
import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import {
  Search,
  CheckCircle2,
  Users,
  FileText,
  PieChart,
  Compass,
  Lightbulb,
  Sliders,
  MessageSquare,
  HelpCircle,
  ArrowRight,
} from 'lucide-react';

import HeaderNav from './HeaderNav';
import FooterLan from './FooterLan';

// --- Color Palette from main site ---
const COLORS = {
  primary: '#ce2223',      // Lusidea Red
  primaryDark: '#b01a1b',
  white: '#ffffff',
  gray50: '#f9fafb',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray900: '#111827',
};

// --- Spacing system from main site ---
const SPACING = {
  container: 'max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-8',
  section: 'py-12 md:py-16 lg:py-20',
  sectionCompact: 'py-8 md:py-10 lg:py-12',
};

// Core tool categories with SEO descriptions
const TOOL_CATEGORIES = [
  {
    id: 'all',
    name: 'All Tools',
    icon: Compass,
    description: 'Discover all available innovation tools',
    bgColor: 'bg-gray-50',
  },
  {
    id: 'assessment',
    name: 'Assessment',
    icon: CheckCircle2,
    description: 'Tools for evaluating innovation capabilities',
    bgColor: 'bg-blue-50',
  },
  {
    id: 'ideation',
    name: 'Ideation',
    icon: Lightbulb,
    description: 'Tools to generate and develop new ideas',
    bgColor: 'bg-yellow-50',
  },
  {
    id: 'planning',
    name: 'Planning',
    icon: PieChart,
    description: 'Tools for structuring innovation plans',
    bgColor: 'bg-green-50',
  },
  {
    id: 'forecasting',
    name: 'Forecasting',
    icon: Search,
    description: 'Tools for identifying future trends',
    bgColor: 'bg-orange-50',
  },
];

// Tools data adapted to match provided routes (no priority, no search)
const TOOLS_DATA = [
  {
    id: 'FutureSignalScanner',
    name: 'Future Signal Scanner',
    category: 'forecasting',
    icon: Search,
    shortDesc: 'Identify weak signals of change that could disrupt your market.',
    longDesc:
      'Systematically scan and interpret early indicators of change to detect emerging trends before competitors.',
    useCases: ['Detect early market disruptions', 'Track emerging technologies'],
    output: 'Signal map with impact assessment',
    difficulty: 'Advanced',
    colors: { bg: 'bg-orange-50', text: 'text-orange-700' },
  },
  {
    id: 'InnovationHealthCheck',
    name: 'Innovation Health Check',
    category: 'assessment',
    icon: CheckCircle2,
    shortDesc: 'Evaluate your organization’s innovation capabilities and identify improvement areas.',
    longDesc:
      'A comprehensive assessment across strategy, process, culture, and leadership to benchmark innovation health.',
    useCases: ['Benchmark against industry standards', 'Uncover capability gaps'],
    output: 'Detailed health check report with recommendations',
    difficulty: 'Intermediate',
    colors: { bg: 'bg-blue-50', text: 'text-blue-700' },
  },
  {
    id: 'CustomerJourneyMapper',
    name: 'Customer Journey Mapper',
    category: 'ideation',
    icon: Users,
    shortDesc: 'Visualize your customer’s end-to-end experience to uncover pain points and opportunities.',
    longDesc:
      'Map out each stage of the customer journey, highlight friction points, and identify new innovation areas.',
    useCases: ['Identify user pain points', 'Design customer-centric solutions'],
    output: 'Customer journey map with opportunity areas',
    difficulty: 'Beginner',
    colors: { bg: 'bg-yellow-50', text: 'text-yellow-700' },
  },
  {
    id: 'InnovationCanvasTool',
    name: 'Innovation Canvas Tool',
    category: 'planning',
    icon: FileText,
    shortDesc: 'Translate abstract ideas into structured innovation blueprints.',
    longDesc:
      'A canvas framework for defining value propositions, required resources, and success metrics before execution.',
    useCases: ['Structure promising concepts', 'Align on value propositions'],
    output: 'Complete innovation blueprint',
    difficulty: 'Intermediate',
    colors: { bg: 'bg-green-50', text: 'text-green-700' },
  },
  {
    id: 'InnovationOpportunityMapper',
    name: 'Innovation Opportunity Mapper',
    category: 'planning',
    icon: PieChart,
    shortDesc: 'Map and prioritize innovation opportunities across various horizons.',
    longDesc:
      'Analyze and optimize your innovation portfolio to balance incremental, adjacent, and transformational projects.',
    useCases: ['Balance innovation investments', 'Identify portfolio gaps'],
    output: 'Opportunity map with prioritization',
    difficulty: 'Advanced',
    colors: { bg: 'bg-green-50', text: 'text-green-700' },
  },
  {
    id: 'InnovationChallengeReframer',
    name: 'Innovation Challenge Reframer',
    category: 'ideation',
    icon: Compass,
    shortDesc: 'Turn complex business problems into well-framed innovation challenges.',
    longDesc:
      'Use structured framing techniques to define clear, actionable innovation challenge statements for ideation sessions.',
    useCases: ['Frame focused challenges', 'Align teams on problems'],
    output: 'Challenge statement framework',
    difficulty: 'Beginner',
    colors: { bg: 'bg-yellow-50', text: 'text-yellow-700' },
  },
  {
    id: 'DesignThinking',
    name: 'Design Thinking Workshop Simulator',
    category: 'ideation',
    icon: Lightbulb,
    shortDesc: 'Simulate a full design thinking workshop to generate user-centric solutions.',
    longDesc:
      'Step through empathy, define, ideate, prototype, and test phases in an interactive workshop environment.',
    useCases: ['Practice design thinking steps', 'Run virtual ideation workshops'],
    output: 'Set of user-centered prototypes',
    difficulty: 'Intermediate',
    colors: { bg: 'bg-yellow-50', text: 'text-yellow-700' },
  },
  {
    id: 'SixThinkingHats',
    name: 'Six Thinking Hats Tool',
    category: 'ideation',
    icon: Sliders,
    shortDesc: 'Apply Edward de Bono’s Six Thinking Hats to explore different perspectives.',
    longDesc:
      'Guide teams through six distinct thinking modes—facts, emotions, caution, optimism, creativity, and process—to generate balanced ideas.',
    useCases: ['Overcome biased thinking', 'Generate multi-faceted ideas'],
    output: 'Structured idea list from six perspectives',
    difficulty: 'Beginner',
    colors: { bg: 'bg-yellow-50', text: 'text-yellow-700' },
  },
  {
    id: 'QuestionBank',
    name: 'How Might We / What If Generator',
    category: 'ideation',
    icon: MessageSquare,
    shortDesc: 'Automatically generate “How Might We…” and “What If…” prompts to spark creativity.',
    longDesc:
      'Leverage a dynamic question bank to prompt fresh thinking and drive creative problem solving.',
    useCases: ['Break through creative blocks', 'Spark divergent thinking'],
    output: 'List of customized ideation prompts',
    difficulty: 'Beginner',
    colors: { bg: 'bg-yellow-50', text: 'text-yellow-700' },
  },
];

// --- Tool Card Component ---
const ToolCard = React.memo(({ tool }) => {
  const { id, name, category, icon: Icon, shortDesc, difficulty, colors, useCases, output } = tool;
  const categoryName = TOOL_CATEGORIES.find((c) => c.id === category)?.name || '';

  return (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 flex flex-col h-full">
      {/* Card Header */}
      <div className="p-3 sm:p-4 border-b border-gray-100">
        <div className="flex items-center">
          <div className={`w-10 h-10 rounded-full ${colors.bg} flex items-center justify-center mr-3 flex-shrink-0`}>
            {React.createElement(Icon, { size: 18, className: colors.text })}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-base sm:text-lg">{name}</h3>
            <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
              <span>{categoryName}</span>
              <span className="w-1 h-1 rounded-full bg-gray-300" />
              <span
                className={`px-1.5 py-0.5 rounded text-xs ${
                  difficulty === 'Beginner'
                    ? 'bg-green-100 text-green-800'
                    : difficulty === 'Intermediate'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-purple-100 text-purple-800'
                }`}
              >
                {difficulty}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Card Body */}
      <div className="p-3 sm:p-4 flex-grow">
        <p className="text-gray-600 text-xs sm:text-sm mb-3">{shortDesc}</p>
        <div className="mb-3">
          <h4 className="text-xs font-semibold text-gray-700 mb-1.5">Key Use Cases:</h4>
          <ul className="space-y-1">
            {useCases.slice(0, 2).map((useCase, i) => (
              <li key={i} className="flex items-start">
                <CheckCircle2 size={12} className="text-primary flex-shrink-0 mt-0.5 mr-1.5" />
                <span className="text-xs text-gray-600 leading-tight">{useCase}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-xs font-semibold text-gray-700 mb-1.5">Deliverable:</h4>
          <div className="bg-gray-50 rounded-md p-2">
            <p className="text-xs text-gray-600">{output}</p>
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="px-3 sm:px-4 pb-3 sm:pb-4 mt-auto">
        <a
          href={`/tools/${id}`}
          className="w-full inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primaryDark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          aria-label={`Use ${name} tool`}
        >
          Use This Tool
          <ArrowRight size={16} className="ml-2" />
        </a>
      </div>
    </div>
  );
});

ToolCard.displayName = 'ToolCard';

// --- Main Showcase Component ---
const LusideaToolsShowcase = () => {
  // State for active category (default to 'all')
  const [activeCategory, setActiveCategory] = useState('all');

  // Filter tools based on active category
  const filteredTools = useMemo(() => {
    if (activeCategory === 'all') {
      return TOOLS_DATA;
    }
    return TOOLS_DATA.filter((tool) => tool.category === activeCategory);
  }, [activeCategory]);

  // Structured Data for SEO
  const structuredData = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Innovation Tools Library',
      description:
        'Explore our library of innovation tools such as Future Signal Scanner, Innovation Health Check, Customer Journey Mapper, and more.',
      url: 'https://www.yourdomain.com/tools',
      publisher: {
        '@type': 'Organization',
        name: 'Your Company',
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.yourdomain.com/images/logo.png',
        },
      },
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: TOOLS_DATA.map((tool, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'SoftwareApplication',
            name: tool.name,
            description: tool.shortDesc,
            applicationCategory: 'BusinessApplication',
            offers: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'USD',
            },
          },
        })),
      },
    }),
    []
  );

  return (
    <div className="font-sans antialiased text-gray-700 bg-white min-h-screen">
      {/* SEO Metadata */}
      <Helmet>
        <title>Innovation Tools Library | Your Company</title>
        <meta
          name="description"
          content="Explore our library of innovation tools such as Future Signal Scanner, Innovation Health Check, Customer Journey Mapper, and more."
        />
        <meta
          name="keywords"
          content="Future Signal Scanner, Innovation Health Check, Customer Journey Mapper, Innovation Canvas Tool, Innovation Opportunity Mapper, Innovation Challenge Reframer, Design Thinking, Six Thinking Hats, How Might We What If Generator"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.yourdomain.com/tools" />

        {/* Open Graph */}
        <meta property="og:title" content="Innovation Tools Library | Your Company" />
        <meta
          property="og:description"
          content="Access specialized innovation tools to solve your organization’s most pressing innovation challenges."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.yourdomain.com/tools" />
        <meta property="og:image" content="https://www.yourdomain.com/images/tools-library-og.jpg" />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <HeaderNav />

      <main id="main-content">
        {/* Hero Section */}
        <section
          className="pt-20 sm:pt-24 pb-12 sm:pb-16 bg-gradient-to-b from-gray50 via-white to-white"
          aria-labelledby="hero-heading"
        >
          <div className={SPACING.container}>
            <div className="max-w-3xl mx-auto text-center">
              <h1 id="hero-heading" className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
                Strategic <span className="text-primary">Innovation Tools</span> Library
              </h1>
              <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8">
                Access a suite of tools—like Future Signal Scanner, Innovation Health Check, Customer
                Journey Mapper, and more—that help solve your innovation challenges.
              </p>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="py-4 bg-white border-b border-gray-200">
          <div className={SPACING.container}>
            <div className="flex overflow-x-auto scrollbar-hide space-x-2">
              {TOOL_CATEGORIES.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex-shrink-0 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                    activeCategory === category.id
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  aria-pressed={activeCategory === category.id}
                >
                  {React.createElement(category.icon, {
                    size: 16,
                    className: `inline-block mr-1 -mt-0.5 ${
                      activeCategory === category.id ? 'text-white' : 'text-gray-700'
                    }`,
                  })}
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Tools Grid */}
        <section id="tools-grid" className="py-8 sm:py-12 bg-white">
          <div className={SPACING.container}>
            {/* Section Header */}
            <div className="mb-5 sm:mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">
                {activeCategory === 'all'
                  ? 'All Innovation Tools'
                  : TOOL_CATEGORIES.find((c) => c.id === activeCategory)?.name}
              </h2>
              <p className="text-sm sm:text-base text-gray-600">
                {TOOL_CATEGORIES.find((c) => c.id === activeCategory)?.description ||
                  'Browse specialized tools to solve your innovation challenges.'}
              </p>
            </div>

            {filteredTools.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {filteredTools.map((tool) => (
                  <ToolCard key={tool.id} tool={tool} />
                ))}
              </div>
            ) : (
              <div className="text-center py-10 sm:py-16 bg-gray-50 rounded-lg">
                <HelpCircle size={32} className="mx-auto mb-3 text-gray-400" />
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No tools found</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 max-w-md mx-auto">
                  There are no tools in this category right now. Please select a different category.
                </p>
                <button
                  onClick={() => setActiveCategory('all')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primaryDark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  aria-label="Show all tools"
                >
                  Show All Tools
                </button>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-10 sm:py-16 bg-gradient-to-r from-primary via-primaryDark to-primary text-white">
          <div className={SPACING.container}>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">
                Need Help With Your Innovation Strategy?
              </h2>
              <p className="text-base sm:text-xl text-white/90 mb-6 sm:mb-8 max-w-2xl mx-auto">
                Our experts can guide you in selecting the right tools and building a tailored
                approach for your organization.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-5 py-2.5 border border-transparent text-base font-medium rounded-md shadow-md bg-white text-primary hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
                  aria-label="Schedule a consultation"
                >
                  Schedule a Consultation
                  <ArrowRight size={16} className="ml-2" />
                </a>
                <a
                  href="/methodology"
                  className="inline-flex items-center justify-center px-5 py-2.5 border border-white text-base font-medium rounded-md text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
                  aria-label="Learn about our innovation approach"
                >
                  Learn Our Approach
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <FooterLan />

      {/* Hide scrollbar but keep scroll functionality */}
      <style jsx global>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default LusideaToolsShowcase;
